import { useEffect, useState, useCallback, useMemo } from "react";
import ModalComponent from "../../../../components/modal/ModalComponent";
import { Checkbox, Upload, Button } from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import styles from "./uploadAssets.module.css";
import TextComponent from "../../../../components/textComponent/TextComponent";
import { showAlert } from "../../../../Store/Actions/GeneralActions";
import { getUploadsAssetColumns } from "../../../../Store/Actions/AssetsAction";
import { useDispatch, useSelector } from "react-redux";
import Storage from "../../../../utills/AsyncStorage";
import { AssetsMiddleware } from "../../../../Store/Middlewares/AssetsMiddleware";

const COLUMN_OPTIONS = [
  "Name",
  "Make",
  "Model",
  "VIN",
  "License No",
  "Battery Voltage",
  "Trim",
  "Color",
  "Device Name",
  "Device ID",
];

const COMPULSORY_COLUMNS = ["VIN", "Name"];

const UploadAssets = ({ isOpen, setIsOpen }) => {
  const dispatch = useDispatch();

  const columns = useSelector(
    (state) => state.AssetsReducer.getUploadsAssetColumns
  );

  const [step, setStep] = useState(1);
  const [templateDownloaded, setTemplateDownloaded] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [jsonData, setJsonData] = useState(null);

  useEffect(() => {
    const loadColumns = async () => {
      const stored = await Storage.getAssetsColumns("@assetcolumns");
      setSelectedColumns(stored || columns || []);
    };
    loadColumns();
  }, [columns]);

  const allColumns = useMemo(
    () => [...COMPULSORY_COLUMNS, ...selectedColumns],
    [selectedColumns]
  );

  const generateExcelTemplate = useCallback(() => {
    if (selectedColumns.length === 0) {
      dispatch(showAlert({ type: "Error", message: "Select columns" }));
      return;
    }

    const worksheet = XLSX.utils.aoa_to_sheet([allColumns]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Asset Template");

    XLSX.writeFile(workbook, "AssetTemplate.xlsx");

    Storage.setAssetsColumns("@assetcolumns", JSON.stringify(selectedColumns));
    dispatch(getUploadsAssetColumns(selectedColumns));

    setTemplateDownloaded(true);
    setStep(2);
  }, [selectedColumns, allColumns]);

  const saveColumns = useCallback(() => {
    Storage.setAssetsColumns("@assetcolumns", JSON.stringify(selectedColumns));
    dispatch(getUploadsAssetColumns(selectedColumns));

    setTemplateDownloaded(true);
    setStep(2);
  }, [selectedColumns]);

  const handleExcelUpload = useCallback((file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const workbook = XLSX.read(new Uint8Array(e.target.result), {
        type: "array",
      });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      setJsonData(XLSX.utils.sheet_to_json(sheet));
    };

    reader.readAsArrayBuffer(file);
    return false; // prevent auto-upload
  }, []);

  const Submit = () => {
    if (!jsonData || jsonData.length === 0) {
      dispatch(
        showAlert({ type: "Error", message: "Uploaded file is empty." })
      );
      return;
    }

    const uploadedColumns = Object.keys(jsonData[0]);

    const expectedColumns = [...COMPULSORY_COLUMNS, ...selectedColumns];

    const isMatch = expectedColumns.every((col) =>
      uploadedColumns.includes(col)
    );

    if (!isMatch) {
      dispatch(
        showAlert({
          type: "Error",
          message:
            "Uploaded Excel file columns do not match selected template.",
        })
      );
      return;
    }

    const mappedData = jsonData.map((item) => ({
      vin_no: item["VIN"] || "",
      name: item["Name"] || "",
      license_no: String(item["License No"]) || "",
      device_id: item["Device ID"] || "",
      device_name: item["Device Name"] || "",
      make: item["Make"] || "",
      model: item["Model"] || "",
      color: item["Color"] || "",
      trim: item["Trim"] || "",
      battery_volt: item["Battery Voltage"] || "",
    }));

    const emptyVinRows = mappedData.findIndex(
      (item) => !item.vin_no
    );

    if (emptyVinRows !== -1) {
      dispatch(
        showAlert({
          type: "Error",
          message: `VIN number is missing in row ${emptyVinRows + 2}`,
        })
      );
      return;
    }

    dispatch(AssetsMiddleware.uploadAssets(mappedData))
      .then(() => {
        setIsOpen(false);
        setJsonData(null);
      })
      .catch(() => {
        dispatch(showAlert({ type: "Error", message: "Upload failed." }));
      });
  };

  const resetColumns = () => {
    setSelectedColumns([]);
    dispatch(getUploadsAssetColumns([]));
    Storage.removeAssetsColumns();
    setStep(1);
  };

  return (
    <ModalComponent
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      width="700px"
      twoButton={false}
      btnDiv={styles.access_modalBtn}
    >
      {step === 1 && (
        <div>
          <TextComponent
            text="Select Columns to Include in Template"
            className={styles.heading}
          />

          <div className={styles.checkboxs}>
            {COLUMN_OPTIONS.map((col) => {
              const isCompulsory = COMPULSORY_COLUMNS.includes(col);
              return (
                <Checkbox
                  className={styles.check}
                  key={col}
                  disabled={isCompulsory}
                  checked={selectedColumns.includes(col)}
                  onChange={(e) =>
                    setSelectedColumns((prev) =>
                      e.target.checked
                        ? [...prev, col]
                        : prev.filter((item) => item !== col)
                    )
                  }
                >
                  {col}{" "}
                  {isCompulsory && <span style={{ color: "red" }}>*</span>}
                </Checkbox>
              );
            })}
          </div>
          <div className="flex-between">
            <Button style={{ marginTop: 20 }} onClick={generateExcelTemplate}>
              Download Excel Template
            </Button>
            <Button style={{ marginTop: 20 }} onClick={saveColumns}>
              Save and Continue
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <h2>Upload Filled Excel File</h2>

          {templateDownloaded && (
            <div className="flex-between">
              <Button type="link" onClick={generateExcelTemplate}>
                Download Selected Template
              </Button>

              <Button onClick={resetColumns}>Reselect Columns</Button>
            </div>
          )}

          <Upload.Dragger
            beforeUpload={handleExcelUpload}
            accept=".xlsx,.xls"
            multiple={false}
            style={{ padding: 30, marginTop: "20px" }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p>Click or drag Excel file to upload</p>
          </Upload.Dragger>

          {jsonData && (
            <Button style={{ marginTop: 20 }} onClick={Submit}>
              Upload File
            </Button>
          )}
        </div>
      )}
    </ModalComponent>
  );
};

export default UploadAssets;
